/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import { useState, useEffect, useContext } from 'react';

import { useForm } from 'react-hook-form';
import { UIContext } from '../context/UIContext';

import dayjs from 'dayjs';

import ForwardingAddress from './ForwardingAddress';
import MainInfoBuilding from './MainInfoBuilding';
import MoveOutInformation from './MoveOutInformation';
import OccupantDetails from './OccupantDetails';
import TermsAndAgreement from './TermsAndAgreement';
import DialogMessage from './DialogMessage';

import MoveOut_icon from '../assets/img/moveout-icon.png';
import moveOut_icon_white from '../assets/img/kanso_moving-box.png';
import error_icon from './../assets/img/icon_error.png';
import confirm_icon from './../assets/img/icon-confirm.png';
import complete_icon from './../assets/img/icon_complete.png';

const CONFIRM_DIALOG_TEXT = (
  <p>
    Are you sure you want to submit this Notice to Vacate form? A copy of this form will be sent to
    the registered email address of<span className="font-bold"> all residents</span>. <br></br>
    <br></br> After the community has processed your Notice, you will see the status updated in the
    Renewal Dashboard. Please <span className="font-bold">allow up to two business days</span> for
    this status update to display. We will record your Notice to Vacate based on the day you
    submitted this form.
  </p>
);

const SUCCESS_MESSAGE = (
  <p>
    A copy of this Notice to Vacate form has been sent to the registered email address of all
    residents.
  </p>
);

const ERROR_NOT_EMAIL_FOUND = (
  <p>
    Sorry! You cannot submit this form. One of the resident email addresses is blank. This is needed
    to copy all residents on submission of this form. Please reach out to the community for help to
    provide notice of your intent to vacate.
  </p>
);

const NoticeToVacateForm = ({ isOpen }) => {
  const {
    register,
    control,
    handleSubmit,
    clearErrors,
    reset,
    getValues,
    resetField,
    watch,
    setValue,
    formState: { errors, dirtyFields }
  } = useForm();

  const {
    formData,
    openMessageDialog,
    messageDialogOpen,
    setFormNoticeToVacate,
    showSuccessMessage,
    closeMessageDialog
  } = useContext(UIContext);

  const { resident, community, coresidents, occupants, lease } = formData;

  const [disableForm, setDisableForm] = useState();
  const [residentsCoResidentsOccupants, setResidentsCoResidentsOccupants] = useState(1);

  const [messageDialog, setMessageDialog] = useState({
    error: false,
    confirm: false,
    success: false
  });

  const prepareData = () => {
    const data = getValues();

    const validForm = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value != null && value !== '')
    );

    const { isForwardingAddressUnknown } = validForm;

    const trackFormSubmittedPendo = (form) => {
      if (window.pendo && window.pendo.track) {
        try {
          window.pendo.track('NTVFormSubmited', form);
        } catch (error) {
          console.error('Error tracking with Pendo:', error);
        }
      } else {
        console.warn('Pendo is not defined or the track function is not available.');
      }
    };

    const {
      moveOutDate,
      reasonMoveOut,
      residenceTypeMoveIn,
      street,
      city,
      countrySelected,
      addressLine2,
      state,
      zip,
      feedback,
      signature
    } = validForm;

    let formToSubmit = {
      moveOutDate: dayjs(moveOutDate).format(),
      reasonMoveOut: reasonMoveOut.id,
      residenceTypeMoveIn: residenceTypeMoveIn.id,
      feedback: feedback ? feedback.trim() : feedback,
      signature: signature.trim(),
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      submitTsDate: dayjs().format()
    };
    if (!isForwardingAddressUnknown) {
      const address = {
        street: street.trim(),
        city: city.trim(),
        country: countrySelected.countryName,
        state: state.trim(),
        zip: zip.trim()
      };
      if (countrySelected.countryCode !== 'US') {
        address.addressLine2 = addressLine2.trim();
      }

      setFormNoticeToVacate({ ...formToSubmit, address });
      trackFormSubmittedPendo({ ...formToSubmit, address });
    } else {
      setFormNoticeToVacate(formToSubmit);
      trackFormSubmittedPendo(formToSubmit);
    }
  };

  const styles = {
    divClass:
      'bg-white border rounded  xs:mt-5 lg:mt-5  flex-col content-center justify-items-center justify-center items-center '
  };
  const onSubmit = () => {
    prepareData();
    openMessageDialog();
    setMessageDialog({ error: false, confirm: true, success: false });
  };

  const checkEmailsForResidentsAndOccupants = () => {
    const residentsAndCoResidents = [...coresidents, resident];
    const isEmptyObject = (obj) => obj && Object.keys(obj).length === 0;
    const invalidEmails = residentsAndCoResidents
      .flat()
      .map((resident) => resident?.email == null && !isEmptyObject(resident));

    const hasValidsEmail = invalidEmails.every(
      (value) => value === false && resident.email !== null
    );

    setDisableForm(!hasValidsEmail);
    if (!hasValidsEmail) {
      openMessageDialog();
      setMessageDialog({ confirm: false, error: true, success: false });
    } else {
      closeMessageDialog();
      setMessageDialog({ confirm: false, error: false, success: false });
    }
  };

  const countResidentsCoresidentsOccupants = () => {
    const residentsCoResidentsOccupants = [...coresidents, ...occupants, resident];
    setResidentsCoResidentsOccupants(residentsCoResidentsOccupants.length);
  };

  useEffect(() => {
    if (showSuccessMessage)
      return setMessageDialog({ confirm: false, error: false, success: true });
    if (coresidents && coresidents.length > 0) {
      checkEmailsForResidentsAndOccupants();
      countResidentsCoresidentsOccupants();
    }
  }, [showSuccessMessage, resident]);

  return (
    <div className=" bg-transparent xl:border-0 xl:p-0 rounded-xl">
      <div className=" bg-lavanderPink p-2 justify-around items-center lg:flex  ">
        <div className="lg:pl-3 md:pl-8 pl-3">
          <img
            src={community?.brand === 'kanso' ? moveOut_icon_white : MoveOut_icon}
            className="w-[24px] lg:w-[2.2rem] float-left "
            alt="moveOut_Icon"
          />
        </div>
        <div className="w-full  text-center  lg:flex  lg:justify-between">
          <div>
            <p
              className={
                community?.brand === 'ava'
                  ? 'font-costa text-base pl-5 mr-5  lg:text-header text-headerFormColor'
                  : 'font-utopia text-base pl-5 mr-5  lg:text-header text-headerFormColor'
              }>
              Notice To Vacate Form
            </p>
          </div>
          <div className="lg:flex lg:items-center lg:pr-2">
            <p className="font-proxima text-tiny lg:text-subHeader font-bold text-headerFormColor">
              Occupants ({residentsCoResidentsOccupants})
            </p>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <p className="font-proxima font-normal text-[#31343A] text-tiny lg:text-sm md:text-xs">
          <span className="font-semibold">Please Note:</span> Your lease requires you to provide us
          with notice of your intent to vacate. A copy of this form will be sent to the community,
          in addition to all residents’ emails listed below.
          <br />
          <br />
          By submitting this form, you confirm that you are submitting on behalf of all residents
          identified in the lease for this apartment home and indemnify AvalonBay for any related
          liability.
        </p>
        <br />
        <p className="font-proxima text-tiny lg:text-sm">
          <span className="text-[#F5222D]">*</span> Required
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} aria-label="form">
        <div className="lg:w-full lg:static md:z-0  md:w-[90%] md:absolute">
          <div className={`${styles.divClass} ${isOpen ? 'md:mt-[66%]' : 'md:mt-[10%]'}  `}>
            <MainInfoBuilding />
            {coresidents && <OccupantDetails coresidents={coresidents} />}
            <MoveOutInformation
              register={register}
              errors={errors}
              control={control}
              setValue={setValue}
              leaseExpirationDate={lease?.leaseExpirationDate}
              disableForm={disableForm}
            />
            <ForwardingAddress
              resetField={resetField}
              register={register}
              errors={errors}
              reset={reset}
              clearErrors={clearErrors}
              disableForm={disableForm}
              watch={watch}
              setValue={setValue}
              dirtyFields={dirtyFields}
              control={control}
            />
            <TermsAndAgreement
              resetField={resetField}
              register={register}
              errors={errors}
              reset={reset}
              clearErrors={clearErrors}
              disableForm={disableForm}
              setValue={setValue}
            />
          </div>
          <div className="flex justify-around md:justify-end mt-5 font-proxima font-semibold   lg:mt-5 mb-5 ">
            <div className="md:mr-5">
              <a href={`${process.env.REACT_APP_RENEWALS_URL}/dashboard`}>
                <button
                  type="button"
                  className="bg-white w-[120px] h-[44px] lg:w-[74px] lg:h-[40px] rounded-md border border-input-border hover:border-primary hover:text-primary">
                  Cancel
                </button>
              </a>
            </div>
            <button
              className="btn-bg hover:bg-hover w-[120px] h-[44px] lg:w-[178px] lg:h-[40px] text-contrast rounded-md"
              type="submit"
              aria-label="submitBtn"
              disabled={disableForm}>
              Confirm
            </button>
          </div>
        </div>
      </form>
      {messageDialog.error && (
        <DialogMessage
          open={messageDialogOpen}
          icon={error_icon}
          title={'Email Not Found !'}
          body={ERROR_NOT_EMAIL_FOUND}
          confirmButton={'Got It!'}
        />
      )}
      {messageDialog.confirm && (
        <DialogMessage
          open={messageDialogOpen}
          icon={confirm_icon}
          title={'Confirm Notice To Vacate ?'}
          body={CONFIRM_DIALOG_TEXT}
          confirmButton={'Confirm'}
          cancelButton={'Cancel'}
          submitForm={true}
        />
      )}
      {messageDialog.success && (
        <DialogMessage
          open={messageDialogOpen}
          icon={complete_icon}
          title={'Email Sent!'}
          body={SUCCESS_MESSAGE}
          confirmButton={'Got it'}
          goHome={true}
        />
      )}
    </div>
  );
};

export default NoticeToVacateForm;
